import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { ASSETS } from "../assets";
import { Globe } from "lucide-react";

const SOCIAL_LINKS = [
  {
    key: "x",
    label: "X",
    href: "https://x.com/ShibaInuWorld_",
    hover: "#ffffff",
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
        <path d="M18.244 2H21l-6.55 7.49L22 22h-6.79l-4.74-6.21L4.6 22H2l7.01-8.02L2 2h6.97l4.32 5.71L18.244 2Zm-1.19 18.4h1.75L7.04 3.5H5.18l11.87 16.9Z" />
      </svg>
    ),
  },
  {
    key: "youtube",
    label: "YouTube",
    href: "https://youtube.com/@satoshin_gmx",
    hover: "#ff0033",
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1c.4-1.9.5-3.8.5-5.8s-.1-3.9-.5-5.8ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z" />
      </svg>
    ),
  },
  {
    key: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/share/1Gt7FdeagG/",
    hover: "#1877f2",
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
        <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.14 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.57v1.88h2.78l-.44 2.91h-2.34V22c4.78-.8 8.44-4.94 8.44-9.94Z" />
      </svg>
    ),
  },
  {
    key: "website",
    label: "rebelbtc.com",
    href: "https://rebelbtc.com",
    hover: "#00d4ff",
    svg: <Globe className="h-3.5 w-3.5" />,
  },
];

export const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer
      className="relative w-full border-t border-white/5 bg-black py-12"
      data-testid="main-footer"
    >
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 md:px-12 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex items-center gap-4">
          <img src={ASSETS.logos.rebel} alt="REBEL" className="h-12 w-auto md:h-14" />
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
              REBEL
            </p>
            <p className="mt-1 text-xs text-white/40">{t.footer.tagline}</p>
            {/* SOCIAL ICONS — official RE₿EL channels */}
            <div className="mt-3 flex items-center gap-2" data-testid="footer-socials">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer me"
                  aria-label={s.label}
                  title={s.label}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white/55 transition-all hover:scale-105"
                  data-testid={`footer-social-${s.key}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${s.hover}66`;
                    e.currentTarget.style.color = s.hover;
                    e.currentTarget.style.boxShadow = `0 0 14px ${s.hover}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.55)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {s.svg}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 text-xs md:grid-cols-3 md:gap-12">
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-white/80">
              {t.footer.ecosystem}
            </p>
            <ul className="mt-3 space-y-2 text-white/50">
              <li>
                <a href="#trendia" className="transition-colors hover:text-white" data-testid="footer-trendia">
                  TrendIA
                </a>
              </li>
              <li>
                <a href="#topwallet" className="transition-colors hover:text-white" data-testid="footer-topwallet">
                  TopWallet
                </a>
              </li>
              <li>
                <Link to="/shadow/tokens" className="transition-colors hover:text-white" data-testid="footer-rbl-coin">
                  RE₿EL Coin
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-white/80">
              {t.footer.resources || "Resources"}
            </p>
            <ul className="mt-3 space-y-2 text-white/50">
              <li>
                <Link to="/academy" className="transition-colors hover:text-white" data-testid="footer-academy">
                  Academia RE₿EL
                </Link>
              </li>
              <li>
                <Link to="/simulator" className="transition-colors hover:text-white" data-testid="footer-simulator">
                  Market Simulator™
                </Link>
              </li>
              <li>
                <Link to="/blog" className="transition-colors hover:text-white" data-testid="footer-blog">
                  {t.footer.blog || "Blog"}
                </Link>
              </li>
              <li>
                <Link to="/shadow" className="transition-colors hover:text-amber-200" data-testid="footer-shadow">
                  SHADOW NETWORK™
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-white/80">{t.footer.legal}</p>
            <ul className="mt-3 space-y-2 text-white/50">
              <li>
                <Link to="/terms" className="transition-colors hover:text-white" data-testid="footer-terms">
                  {t.footer.terms}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="transition-colors hover:text-white" data-testid="footer-privacy">
                  {t.footer.privacy}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="transition-colors hover:text-white" data-testid="footer-contact">
                  {t.footer.contact}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-10 max-w-7xl border-t border-white/5 px-6 pt-6 md:px-12">
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">
          © {new Date().getFullYear()} REBEL. {t.footer.rights}
        </p>
      </div>
    </footer>
  );
};
